"use client"
import { signOut, useSession } from "next-auth/react"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import { Magic } from "magic-sdk"
import { polygon } from "viem/chains"
import logo from "../../../public/images/logo/logo.svg"
import { usePathname } from "next/navigation"
import menuData from "./menuData"
import DropDown from "./DropDown"
import { useDictionary } from "@/libs/DictionaryProvider"
import LocaleSwitcher from "../locale-switcher"
import { Locale } from "../../../i18n-config"
import Sidebar from "../Sidebar"
import MobileNav from "../mobile-nav"
import { useUser } from "@auth0/nextjs-auth0/client"
import Menu from "@/base-components/Menu"
import Lucide from "@/base-components/Lucide"
import { twMerge } from "tailwind-merge"
import clsx from "clsx"
import { useUserProfile } from "@/hooks/useUserProfile"
import Slideover from "@/base-components/Slideover"
import {
  MAGIC_AUTH_SUB_KEY,
  POLYGON_RPC_URL,
} from "@/components/OddsScreen/trade-wallet/constants"
import { getRuntimeEnv } from "@/libs/runtimeEnv"
import posthog from "posthog-js"
import { roles } from "@/utils/constants"

const MOBILE_NAV_BREAKPOINT = 1024
const EDGE_SWIPE_START_WIDTH = 32
const EDGE_SWIPE_OPEN_THRESHOLD = 72
const EDGE_SWIPE_MAX_VERTICAL_DELTA = 56

const Header = ({ lang }: { lang: Locale }) => {
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [stickyMenu, setStickyMenu] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const { user, error, isLoading } = useUser()
  const edgeSwipeStartXRef = useRef<number | null>(null)
  const edgeSwipeStartYRef = useRef<number | null>(null)
  const edgeSwipeTrackingRef = useRef(false)

  const pathUrl = usePathname()
  const { navigation } = useDictionary()
  // Sticky menu
  const handleStickyMenu = () => {
    if (window.scrollY >= 80) {
      setStickyMenu(true)
    } else {
      setStickyMenu(false)
    }
  }

  const { data: profile, isLoading: loading } = useUserProfile()

  useEffect(() => {
    window.addEventListener("scroll", handleStickyMenu)
    return () => {
      window.removeEventListener("scroll", handleStickyMenu)
    }
  }, [])

  useEffect(() => {
    if (user?.sub) {
      posthog.identify(user.sub, {
        email: user.email,
        name: user.name,
      })
    }
  }, [user?.sub])

  useEffect(() => {
    if (typeof window === "undefined") return

    const resetSwipeState = () => {
      edgeSwipeStartXRef.current = null
      edgeSwipeStartYRef.current = null
      edgeSwipeTrackingRef.current = false
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (window.innerWidth >= MOBILE_NAV_BREAKPOINT || navigationOpen) {
        resetSwipeState()
        return
      }

      const touch = event.touches[0]
      if (!touch) {
        resetSwipeState()
        return
      }

      if (touch.clientX < window.innerWidth - EDGE_SWIPE_START_WIDTH) {
        resetSwipeState()
        return
      }

      edgeSwipeStartXRef.current = touch.clientX
      edgeSwipeStartYRef.current = touch.clientY
      edgeSwipeTrackingRef.current = true
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (!edgeSwipeTrackingRef.current) return

      const touch = event.touches[0]
      const startX = edgeSwipeStartXRef.current
      const startY = edgeSwipeStartYRef.current

      if (!touch || startX === null || startY === null) {
        resetSwipeState()
        return
      }

      const horizontalDelta = startX - touch.clientX
      const verticalDelta = Math.abs(touch.clientY - startY)

      if (verticalDelta > EDGE_SWIPE_MAX_VERTICAL_DELTA || horizontalDelta < 0) {
        resetSwipeState()
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (!edgeSwipeTrackingRef.current) {
        resetSwipeState()
        return
      }

      const touch = event.changedTouches[0]
      const startX = edgeSwipeStartXRef.current
      const startY = edgeSwipeStartYRef.current

      if (!touch || startX === null || startY === null) {
        resetSwipeState()
        return
      }

      const horizontalDelta = startX - touch.clientX
      const verticalDelta = Math.abs(touch.clientY - startY)

      if (
        horizontalDelta >= EDGE_SWIPE_OPEN_THRESHOLD &&
        verticalDelta <= EDGE_SWIPE_MAX_VERTICAL_DELTA
      ) {
        setNavigationOpen(true)
      }

      resetSwipeState()
    }

    const addTouchListenerOptions: AddEventListenerOptions = { passive: true }

    window.addEventListener(
      "touchstart",
      handleTouchStart,
      addTouchListenerOptions,
    )
    window.addEventListener(
      "touchmove",
      handleTouchMove,
      addTouchListenerOptions,
    )
    window.addEventListener("touchend", handleTouchEnd, addTouchListenerOptions)
    window.addEventListener("touchcancel", resetSwipeState)

    return () => {
      window.removeEventListener("touchstart", handleTouchStart, false)
      window.removeEventListener("touchmove", handleTouchMove, false)
      window.removeEventListener("touchend", handleTouchEnd, false)
      window.removeEventListener("touchcancel", resetSwipeState)
    }
  }, [navigationOpen])

  const handleLogout = async () => {
    posthog.capture("user_signed_out")
    posthog.reset()
    setLoggingOut(true)
    try {
      const runtimeEnv = getRuntimeEnv()
      const runtimeKey =
        runtimeEnv?.MAGIC_API_KEY || runtimeEnv?.NEXT_PUBLIC_MAGIC_API_KEY
      const apiKey = runtimeKey
      if (apiKey) {
        const magic = new Magic(apiKey, {
          network: { rpcUrl: POLYGON_RPC_URL, chainId: polygon.id },
        })
        await magic.user.logout()
      }
    } catch (error) {
      console.error("Failed to clear Magic session on logout:", error)
    } finally {
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(MAGIC_AUTH_SUB_KEY)
      }
      window.location.assign(`/auth/logout`)
    }
  }

  const UserIcon = ({ className }) => {
    return (
      <div className={className}>
        <Menu>
          <Menu.Button className="bg-dark-6 block w-10 h-10 overflow-hidden rounded-full shadow-lg image-fit zoom-in intro-x">
            {user?.name[0]?.toUpperCase()}
          </Menu.Button>
          <Menu.Items
            data-onboarding="profile-menu"
            className="w-56 mt-px relative bg-dark-6 before:block before:absolute before:bg-black before:inset-0 before:rounded-md before:z-[-1] text-white"
          >
            <Menu.Header className="font-normal">
              <div className="font-medium">{`${profile?.firstName ? profile?.firstName : ""} ${profile?.lastName ? profile?.lastName : ""}`}</div>
              <div className="text-xs text-white/70 mt-0.5 dark:text-slate-500">
                {profile?.email}
              </div>
            </Menu.Header>
            <Menu.Divider className="bg-white/[0.08]" />
            <Menu.Item as="div" className="hover:bg-white/5 ">
              <Link className="flex h-full w-full" href={`/${lang}/profile`}>
                <Lucide icon="User" className="w-4 h-4 mr-2 my-auto" />
                Profile
              </Link>
            </Menu.Item>

            {((user?.[roles] as string[]) ?? []).includes("admin") && (
              <>
                <Menu.Divider className="bg-white/[0.08]" />
                <Menu.Item as="div" className="hover:bg-white/5">
                  <Link className="flex h-full w-full" href={`/${lang}/cards-admin`}>
                    <Lucide icon="CreditCard" className="w-4 h-4 mr-2 my-auto" />
                    Cards Admin
                  </Link>
                </Menu.Item>
                <Menu.Item as="div" className="hover:bg-white/5">
                  <Link className="flex h-full w-full" href={`/${lang}/transactions-management`}>
                    <Lucide icon="ArrowLeftRight" className="w-4 h-4 mr-2 my-auto" />
                    Transactions
                  </Link>
                </Menu.Item>
                <Menu.Item as="div" className="hover:bg-white/5">
                  <Link className="flex h-full w-full" href={`/${lang}/sportsfunded-admin`}>
                    <Lucide icon="ShieldCheck" className="w-4 h-4 mr-2 my-auto" />
                    Sports Funded
                  </Link>
                </Menu.Item>
              </>
            )}

            <Menu.Divider className="bg-white/[0.08]" />
            <Menu.Item as="div" className="hover:bg-white/5 ">
              <button
                type="button"
                className="flex w-full h-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                <Lucide
                  icon={loggingOut ? "Loader" : "ToggleRight"}
                  className={`w-4 h-4 mr-2 my-auto ${loggingOut ? "animate-spin" : ""}`}
                />
                {loggingOut ? "Signing out..." : navigation.signout}
              </button>
            </Menu.Item>
          </Menu.Items>
        </Menu>
      </div>
    )
  }

  return (
    <>
      <header
        className={`site-header-gradient fixed left-0 top-0 w-full z-9999 border-b border-white/[0.14] ${
          stickyMenu
            ? "backdrop-blur-lg shadow-[0_16px_45px_rgba(3,0,20,0.35)] !py-3 lg:!py-0 transition duration-100 before:absolute before:w-full before:h-[1px] before:bottom-0 before:left-0 before:features-row-border"
            : "py-4 lg:py-0"
        }`}
      >
        <div className="max-w-[1400px] min-h-[36px] lg:h-[80px] mx-auto px-4 sm:px-4 2xl:px-0 lg:flex items-center justify-between relative">
          <div className="w-full lg:w-1/4 flex items-center justify-between">
            <Link href={`/${lang}`} className="ml-3 inline-block sm:ml-4">
              <img
                src={logo.src}
                alt="Logo"
                width={185}
                height={51}
                className="block h-auto w-[158px] max-w-none sm:w-[185px]"
              />
            </Link>

            <div className="flex">
              {user && <UserIcon className={"lg:hidden block mr-2"} />}
              <button
                onClick={() => setNavigationOpen(!navigationOpen)}
                data-onboarding="mobile-top-nav-menu"
                className="lg:hidden block outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0"
              >
                <span className="block relative cursor-pointer w-5.5 h-5.5">
                  <span className="du-block absolute right-0 w-full h-full">
                    <span
                      className={`block relative top-0 left-0 bg-white rounded-sm h-0.5 my-1 ease-in-out duration-200 delay-[0] ${
                        !navigationOpen ? "!w-full delay-300" : "w-0"
                      }`}
                    ></span>
                    <span
                      className={`block relative top-0 left-0 bg-white rounded-sm h-0.5 my-1 ease-in-out duration-200 delay-150 ${
                        !navigationOpen ? "!w-full delay-400" : "w-0"
                      }`}
                    ></span>
                    <span
                      className={`block relative top-0 left-0 bg-white rounded-sm h-0.5 my-1 ease-in-out duration-200 delay-200 ${
                        !navigationOpen ? "!w-full delay-500" : "w-0"
                      }`}
                    ></span>
                  </span>
                  <span className="du-block absolute right-0 w-full h-full rotate-45">
                    <span
                      className={`block bg-white rounded-sm ease-in-out duration-200 delay-300 absolute left-2.5 top-0 w-0.5 h-full ${
                        !navigationOpen ? "!h-0 delay-[0]" : "h-full"
                      }`}
                    ></span>
                    <span
                      className={`block bg-white rounded-sm ease-in-out duration-200 delay-400 absolute left-0 top-2.5 w-full h-0.5 ${
                        !navigationOpen ? "!h-0 delay-200" : "h-0.5"
                      }`}
                    ></span>
                  </span>
                </span>
              </button>
            </div>
          </div>
          {/* max-h-[400px] */}
          <Slideover
            size="xl"
            open={navigationOpen}
            onClose={() => {
              setNavigationOpen(false)
            }}
          >
            <Slideover.Panel
              data-onboarding="mobile-nav-drawer"
              className=" !w-[300px]  bg-dark-8"
            >
              <div
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault()
                  setNavigationOpen(false)
                }}
                className="absolute top-0 left-0 right-auto mt-4 -ml-12"
                // href="#"
              >
                <Lucide icon="X" className="w-8 h-8 text-slate-400" />
              </div>
              <Slideover.Title>
                <div className="flex w-full justify-end">
                  <LocaleSwitcher lang={lang} />

                  <div className="flex items-center gap-6 ml-1 lg:mt-0 lg:ml-6">
                    {(function () {
                      // if (loading) return <>...</>
                      // if (profile && !loading) {
                      //   return <UserIcon className={"hidden lg:block"} />
                      // }
                      if (!user && !isLoading) {
                        return (
                          <>
                            <Link
                              prefetch={false}
                              href={`/auth/login`}
                              onClick={() => posthog.capture("sign_in_clicked", { location: "mobile_nav" })}
                              className="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                            >
                              {navigation.signin}
                            </Link>
                          </>
                        )
                      }
                    })()}
                  </div>
                </div>
              </Slideover.Title>
              <Slideover.Description>
                <MobileNav lang={lang} setNavigationOpen={setNavigationOpen} />
              </Slideover.Description>
            </Slideover.Panel>
          </Slideover>
          <div
            className={`w-full lg:w-3/4 h-0 lg:h-auto invisible lg:visible lg:flex justify-end ${
              navigationOpen
                ? "!visible shadow-lg relative !h-auto max-h-[500px] overflow-y-scroll rounded-md mt-2 p-7.5"
                : ""
            }`}
          >
            {/* {navigationOpen && ( */}
            <></>
            {/* )} */}
            {!navigationOpen && (
              <div className="flex w-full justify-end">
                <LocaleSwitcher lang={lang} />

                <div className="flex items-center gap-6 ml-1 lg:mt-0 lg:ml-6">
                  {(function () {
                    if (loading) return <>...</>
                    if (profile && !loading) {
                      return <UserIcon className={"hidden lg:block"} />
                    }
                    if (!user && !isLoading) {
                      return (
                        <>
                          <Link
                            prefetch={false}
                            href={`/auth/login`}
                            onClick={() => posthog.capture("sign_in_clicked", { location: "desktop_nav" })}
                            className="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                          >
                            {navigation.signin}
                          </Link>
                        </>
                      )
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
